import React, { useCallback, useEffect, useState } from 'react';
import { FieldType } from './field.types';
import { Autocomplete, FormControl, FormHelperText, TextField } from '@mui/material';
import { useFormValidation } from '../context/FormValidation';
import { useSingleForm } from '../context/SingleForm';

type MultiSelectProps = FieldType & {
  values?: string[];
};

export const MultiSelectField: React.FC<MultiSelectProps> = ({
  required,
  fieldKey,
  name,
  defaultValue,
  description,
  values,
  onChangeFieldValue,
}) => {
  const [fieldValue, setFieldValue] = useState<string[]>(
    Array.isArray(defaultValue) ? defaultValue : defaultValue ? [defaultValue] : []
  );
  const [isValid, setIsValid] = useState<boolean>(true);
  const { showError, updateFieldValidation } = useFormValidation();
  const { formIndex } = useSingleForm();

  const setFieldValidity = useCallback(
    (newIsValid: boolean) => {
      setIsValid(newIsValid);
      updateFieldValidation(`${formIndex}-${fieldKey}`, newIsValid);
    },
    [updateFieldValidation, formIndex, fieldKey]
  );

  useEffect(() => {
    setFieldValidity(required ? !!defaultValue?.length : true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isError = showError && !isValid;

  const onChange = useCallback(
    (event: any, value: string[]) => {
      const newValue = value.filter((val: any) => val !== undefined);
      onChangeFieldValue(fieldKey, newValue);
      setFieldValue(newValue);
      setFieldValidity(newValue.length > 0);
    },
    [onChangeFieldValue, fieldKey, setFieldValidity]
  );

  return (
    <FormControl>
      <Autocomplete
        id={`${formIndex}-${fieldKey}`}
        multiple
        disablePortal
        value={fieldValue}
        options={values || []}
        getOptionLabel={option => option}
        defaultValue={defaultValue || []}
        onChange={onChange}
        renderInput={params => <TextField {...params} error={isError} label={name} required={required} />}
      />
      <FormHelperText>{description}</FormHelperText>
    </FormControl>
  );
};
